import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import AdminLayout from "./AdminLayout";
import Login from "./Login";
import AuthCallback from "./AuthCallback";
import Dashboard from "./pages/Dashboard";
import KVEditor from "./pages/KVEditor";
import I18nEditor from "./pages/I18nEditor";
import UsersAdmin from "./pages/Users";
import { CasesList, CaseEditor } from "./pages/Cases";
import { InsightsList, InsightEditor } from "./pages/Insights";
import CollectionAdmin from "./pages/Collection";
import MediaLibrary from "./pages/Media";
import Versions from "./pages/Versions";
import Branding from "./pages/Branding";
import Analytics from "./pages/Analytics";
import ContactEditor from "./pages/ContactEditor";
import FooterEditor from "./pages/FooterEditor";
import UTMBuilder from "./pages/UTMBuilder";
import SobreEditor from "./pages/SobreEditor";
import ConciergeLogs from "./pages/ConciergeLogs";
import TestimonialsEditor from "./pages/TestimonialsEditor";
import SobreVideosEditor from "./pages/SobreVideosEditor";
import ContactExtrasEditor from "./pages/ContactExtrasEditor";
import WeAreThumbnailsEditor from "./pages/WeAreThumbnailsEditor";
import AuthorBiosEditor from "./pages/AuthorBiosEditor";
import CertificationsEditor from "./pages/CertificationsEditor";
import TimelineEditor from "./pages/TimelineEditor";
import SEOEditor from "./pages/SEOEditor";
import PopupsEditor from "./pages/PopupsEditor";
import ConciergeEditor from "./pages/ConciergeEditor";
import TaxonomiesEditor from "./pages/TaxonomiesEditor";
import HeroCasesEditor from "./pages/HeroCasesEditor";
import ClientsAuthEditor from "./pages/ClientsAuthEditor";
import ClientProductsEditor from "./pages/ClientProductsEditor";
import ClientCampaignsEditor from "./pages/ClientCampaignsEditor";
import ClientDocumentsEditor from "./pages/ClientDocumentsEditor";
import ClientFinancialsEditor from "./pages/ClientFinancialsEditor";
import ClientTimelineEditor from "./pages/ClientTimelineEditor";
import ClientDemandsView from "./pages/ClientDemandsView";
import ClientBriefingConfigEditor from "./pages/ClientBriefingConfigEditor";
import ClientIntegrationsEditor from "./pages/ClientIntegrationsEditor";
import ClientNPSEditor from "./pages/ClientNPSEditor";
import ClientTalkieEditor from "./pages/ClientTalkieEditor";
import ClientArchiveEditor from "./pages/ClientArchiveEditor";
import BrazilMapEditor from "./pages/BrazilMapEditor";
import InternationalPresenceEditor from "./pages/InternationalPresenceEditor";
import Timeline38Editor from "./pages/Timeline38Editor";
import ChatbotConfigEditor from "./pages/ChatbotConfigEditor";
import ChatbotLeadsList from "./pages/ChatbotLeadsList";
import CareersConfigEditor from "./pages/CareersConfigEditor";
import InsightsConfigEditor from "./pages/InsightsConfigEditor";
import { LandingPagesList, LandingPageEditor } from "./pages/LandingPages";
import TranslateAll from "./pages/TranslateAll";
import GeoAnalytics from "./pages/GeoAnalytics";
import LeadsRepository from "./pages/LeadsRepository";
import { Toaster } from "sonner";

export default function AdminApp() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="callback" element={<AuthCallback />} />
        <Route element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="site" element={<Navigate to="/admin/kv/site" replace />} />
          <Route path="i18n" element={<I18nEditor />} />
          <Route path="stats" element={<Navigate to="/admin/kv/stats" replace />} />
          <Route path="pillars" element={<Navigate to="/admin/kv/pillars" replace />} />
          <Route path="home_sections" element={<Navigate to="/admin/kv/home_sections" replace />} />
          <Route path="users" element={<UsersAdmin />} />
          <Route path="kv/:key" element={<KVEditor />} />
          <Route path="cases" element={<CasesList />} />
          <Route path="cases/:id" element={<CaseEditor />} />
          <Route path="insights" element={<InsightsList />} />
          <Route path="insights/:id" element={<InsightEditor />} />
          <Route path="branding" element={<Branding />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="contact" element={<ContactEditor />} />
          <Route path="contact-extras" element={<ContactExtrasEditor />} />
          <Route path="footer" element={<FooterEditor />} />
          <Route path="utm" element={<UTMBuilder />} />
          <Route path="sobre" element={<SobreEditor />} />
          <Route path="sobre-videos" element={<SobreVideosEditor />} />
          <Route path="testimonials" element={<TestimonialsEditor />} />
          <Route path="we-are" element={<WeAreThumbnailsEditor />} />
          <Route path="authors" element={<AuthorBiosEditor />} />
          <Route path="timeline" element={<TimelineEditor />} />
          <Route path="certifications" element={<CertificationsEditor />} />
          <Route path="seo" element={<SEOEditor />} />
          <Route path="popups" element={<PopupsEditor />} />
          <Route path="mordomo" element={<ConciergeEditor />} />
          <Route path="taxonomies" element={<TaxonomiesEditor />} />
          <Route path="hero-cases" element={<HeroCasesEditor />} />
          <Route path="brazil-map" element={<BrazilMapEditor />} />
          <Route path="international" element={<InternationalPresenceEditor />} />
          <Route path="timeline-38" element={<Timeline38Editor />} />
          <Route path="clients-auth" element={<ClientsAuthEditor />} />
          <Route path="client-products" element={<ClientProductsEditor />} />
          <Route path="client-campaigns" element={<ClientCampaignsEditor />} />
          <Route path="client-documents" element={<ClientDocumentsEditor />} />
          <Route path="client-financials" element={<ClientFinancialsEditor />} />
          <Route path="client-timeline" element={<ClientTimelineEditor />} />
          <Route path="client-demands" element={<ClientDemandsView />} />
          <Route path="client-briefing" element={<ClientBriefingConfigEditor />} />
          <Route path="client-integrations" element={<ClientIntegrationsEditor />} />
          <Route path="client-nps" element={<ClientNPSEditor />} />
          <Route path="client-talkie" element={<ClientTalkieEditor />} />
          <Route path="client-archive" element={<ClientArchiveEditor />} />
          <Route path="concierge" element={<ConciergeLogs />} />
          <Route path="chatbot" element={<ChatbotConfigEditor />} />
          <Route path="chatbot-leads" element={<ChatbotLeadsList />} />
          <Route path="careers-config" element={<CareersConfigEditor />} />
          <Route path="insights-config" element={<InsightsConfigEditor />} />
          <Route path="landing-pages" element={<LandingPagesList />} />
          <Route path="landing-pages/:id" element={<LandingPageEditor />} />
          <Route path="translate-all" element={<TranslateAll />} />
          <Route path="geo-analytics" element={<GeoAnalytics />} />
          <Route path="leads" element={<LeadsRepository />} />
          <Route path=":coll" element={<CollectionAdmin />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="versions" element={<Versions />} />
        </Route>
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AuthProvider>
  );
}
